import { alpha, Box, Button, Theme, Typography } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, PeopleAltOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TimeUtils from '../../../utils/TimeUtils';
import { AccountData, OrderData, PropelUserModel } from '../../../api';
import { getTimeFormat } from '../../../common/getDateAndTimeFormat';
import { getUserFullNameFromUserId } from '../../components/UsersMultiSelect';
import { ErrorContext } from './useCheckinScannerMachine';

type SuccessOverlayProps = {
  order: OrderData;
  onDismiss: () => void;
};

type ErrorOverlayProps = {
  error: ErrorContext;
  account: AccountData | null;
  users: PropelUserModel[];
};

const containerSx = (tone: 'primary' | 'error') => ({
  position: 'absolute' as const,
  inset: 0,
  backgroundColor: (theme: Theme) => alpha(theme.palette[tone].main, 0.94),
  color: 'common.white',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  padding: 3,
  textAlign: 'center' as const,
  zIndex: 10,
  gap: 2,
});

const errorKey = (reason: ErrorContext['reason']) => `checkin_scanner.error.${reason}`;

export const SuccessOverlay = ({ order, onDismiss }: SuccessOverlayProps) => {
  const { t } = useTranslation();
  const name = `${order.firstname} ${order.lastname}`.trim();
  return (
    <Box sx={containerSx('primary')}>
      <CheckCircleOutline sx={{ fontSize: 80 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <PeopleAltOutlined fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {order.persons}
        </Typography>
      </Box>
      {order.comment ? (
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {order.comment}
        </Typography>
      ) : null}
      <Button
        variant="contained"
        size="large"
        onClick={onDismiss}
        sx={{
          mt: 1,
          minWidth: 200,
          py: 1.5,
          backgroundColor: 'background.paper',
          color: 'primary.main',
          fontWeight: 700,
          '&:hover': { backgroundColor: 'mb.ground' },
        }}>
        {t('checkin_scanner.next_scan')}
      </Button>
    </Box>
  );
};

export const ErrorOverlay = ({ error, account, users }: ErrorOverlayProps) => {
  const { t } = useTranslation();
  const timeFormat = getTimeFormat(account || undefined);
  const params: Record<string, string | undefined> = {};
  if (error.reason === 'already_checked_in') {
    const mappedName = error.checkedInBy ? getUserFullNameFromUserId(users, error.checkedInBy) : '';
    params.by = mappedName || t('checkin_scanner.unknown_admin');
    params.time = error.checkedInAt
      ? TimeUtils.getLocalTime(error.checkedInAt, account?.timezone || 'utc').format(timeFormat)
      : '';
  }
  const message =
    error.reason === 'network_error' && error.networkMessage ? error.networkMessage : t(errorKey(error.reason), params);

  return (
    <Box sx={containerSx('error')}>
      <ErrorOutline sx={{ fontSize: 88 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {message}
      </Typography>
    </Box>
  );
};
